import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gradePrices, GRADE_COLORS, CEX_VOUCHER_RATIO } from '../utils/priceColor';

const API = process.env.REACT_APP_BACKEND_URL;

const BUYER_LABELS = {
  musicmagpie: 'Music Magpie',
  webuybooks: 'We Buy Books',
  worldofbooks: 'World of Books',
  cex_cash: 'CEX Cash',
  cex_voucher: 'CEX Voucher',
};

const BUYER_ORDER = ['musicmagpie', 'webuybooks', 'worldofbooks', 'cex_cash', 'cex_voucher'];

/**
 * Branded "Prices Found" modal.
 * Colour rules live in `utils/priceColor.js` (shared with AdminLibrary).
 * Canonical CEX edge-case: £0.01 cash + £0.10 voucher → voucher GOLD, cash RED.
 */
export default function PricesFoundModal({ barcode, prices, onClose }) {
  const navigate = useNavigate();
  const { maxCashEquiv, bestValue, grades } = gradePrices(prices);
  const anyPositiveOffer = maxCashEquiv > 0;
  const bestHeadline = bestValue ?? 0;

  // Community Match probe — how many users want this barcode (and how many already matched).
  const [matchInfo, setMatchInfo] = useState({ active_wants: 0, my_matches: 0 });
  useEffect(() => {
    if (!barcode) return;
    fetch(`${API}/api/wanted-list/for-barcode/${barcode}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setMatchInfo(d))
      .catch(() => {});
  }, [barcode]);

  // Normalised rows for rendering.
  const rows = BUYER_ORDER.map((key) => {
    const raw = prices?.[key];
    const value = raw == null ? null : (typeof raw === 'object' && 'value' in raw ? (raw.value == null ? null : Number(raw.value)) : Number(raw));
    return { key, label: BUYER_LABELS[key], value, grade: grades[key] };
  });

  // CEX arbitrage flag — voucher cash-equiv ≥ 1.2× cash (both present & > 0)
  const cexCashVal = rows.find((r) => r.key === 'cex_cash')?.value;
  const cexVoucherVal = rows.find((r) => r.key === 'cex_voucher')?.value;
  const cexArbitrage =
    cexCashVal != null &&
    cexVoucherVal != null &&
    cexVoucherVal > 0 &&
    cexVoucherVal * CEX_VOUCHER_RATIO >= Math.max(cexCashVal, 0.01) * 1.2;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)' }}
      data-testid="prices-found-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-[#0C0C0C] border rounded-2xl shadow-2xl overflow-hidden"
        style={{ borderColor: '#1e1e1e' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: '#1e1e1e' }}>
          <div>
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-black font-bold"
                style={{ background: '#00D26A' }}
              >
                ✓
              </span>
              <h3 className="text-white font-extrabold text-lg tracking-tight">Prices Found</h3>
            </div>
            {barcode && (
              <div className="text-xs text-zinc-500 mt-1 font-mono" data-testid="prices-found-barcode">
                {barcode}
              </div>
            )}
          </div>
          {anyPositiveOffer && (
            <div
              className="text-[10px] font-black tracking-[0.2em] uppercase px-2 py-1 rounded-md"
              style={{ background: '#FFD700', color: '#050505' }}
            >
              Best £{bestHeadline.toFixed(2)}
            </div>
          )}
        </div>

        {/* Community Match banner */}
        {(matchInfo.my_matches > 0 || matchInfo.active_wants > 0) && (
          <div
            className="mx-4 mt-3 mb-1 rounded-lg p-3 flex items-center justify-between gap-3"
            style={{ background: 'rgba(255,215,0,0.08)', border: '1px solid #FFD70055' }}
            data-testid="community-match-banner"
          >
            <div className="min-w-0">
              <div className="text-sm font-extrabold text-white flex items-center gap-2">
                <span>⚡</span>
                {matchInfo.my_matches > 0
                  ? `${matchInfo.my_matches} buyer${matchInfo.my_matches === 1 ? '' : 's'} want this`
                  : `${matchInfo.active_wants} person${matchInfo.active_wants === 1 ? '' : 's'} want this`}
              </div>
              <div className="text-[11px] text-zinc-400 mt-0.5">
                {matchInfo.my_matches > 0
                  ? 'Email them with your eBay listing.'
                  : 'Add prices to unlock arbitrage matches.'}
              </div>
            </div>
            {matchInfo.my_matches > 0 && (
              <button
                onClick={() => {
                  onClose();
                  navigate('/matches');
                }}
                className="text-xs font-black px-3 py-1.5 rounded-full flex-shrink-0"
                style={{ background: '#FFD700', color: '#050505' }}
                data-testid="community-match-view-btn"
              >
                View →
              </button>
            )}
          </div>
        )}

        {/* Rows */}
        <div className="px-4 py-3 space-y-2">
          {rows.map((r) => {
            const s = GRADE_COLORS[r.grade];
            const isBest = r.grade === 'gold';
            return (
              <div
                key={r.key}
                className="flex items-center justify-between rounded-lg px-3 py-3 border"
                style={{ background: s.bg, borderColor: `${s.accent}33` }}
                data-testid={`price-row-${r.key}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    aria-hidden
                    className="flex-shrink-0 w-1.5 h-8 rounded-full"
                    style={{ background: s.accent }}
                  />
                  <span className="text-zinc-200 font-semibold truncate">{r.label}</span>
                  {isBest && (
                    <span
                      className="text-[9px] font-black px-1.5 py-0.5 rounded"
                      style={{ background: '#FFD700', color: '#050505' }}
                    >
                      BEST
                    </span>
                  )}
                  {r.key === 'cex_voucher' && cexArbitrage && (
                    <span
                      className="text-[9px] font-black px-1.5 py-0.5 rounded"
                      style={{ background: '#00D26A', color: '#050505' }}
                      data-testid="cex-arbitrage-flag"
                    >
                      ⚡ ARBITRAGE
                    </span>
                  )}
                </div>
                <div
                  className="font-extrabold text-lg tabular-nums"
                  style={{ color: s.text }}
                  data-testid={`price-value-${r.key}`}
                >
                  {r.value == null ? '—' : `£${r.value.toFixed(2)}`}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex items-center justify-end gap-3" style={{ borderColor: '#1e1e1e' }}>
          <div className="text-[11px] text-zinc-500 mr-auto">
            <span style={{ color: '#FFD700' }}>●</span> Best &nbsp;
            <span style={{ color: '#00D26A' }}>●</span> Viable (≥60%) &nbsp;
            <span style={{ color: '#E04A3F' }}>●</span> Decline
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-full font-bold text-sm"
            style={{
              background: 'linear-gradient(90deg, #00D26A, #FFD700)',
              color: '#050505',
            }}
            data-testid="prices-found-close-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
